import { coalesceStringAllowEmpty } from '@federa/utils/data/coalesce-string-allow-empty';
import { isWebSocketConnected } from '@federa/utils/websocket/is-web-socket-connected';
import { sendWsMessage } from '@federa/utils/websocket/send';
import { isNonNull } from '@federa/utils/validation/type-guards';
import { AGENT_STATUS_WS_TYPE } from '@federa/const/agent-status';
import { toChatMessage, toParticipant } from '@federa/components/messaging/messagingClient.parsers';
import { bareNumberOrNull } from '@federa/const/number-guards';
import type { AgentStatusFrame } from '@federa/types/conversations-chat-structs';
import type { MessagingMessageHandlerContext as MessageHandlerContext } from '@federa/types/ui-chat-messaging';

const MESSAGE_TIMESTAMP_THRESHOLD_MS = 2000;

/** Loosely-typed shapes of the inbound WebSocket frames these handlers narrow. */
type IncomingWsFrame = {
	/** Frame discriminator (`handleAgentStatus` compares it against `AGENT_STATUS_WS_TYPE`). Declared because the former
	 *  `extends Record<string, unknown>` heritage let this read compile against NO declaration at all. */
	type?: string;
	/** An agent-status frame's room (`handleAgentStatus` drops another room's) and a chat frame's, which the read marker
	 *  deliberately does NOT echo back. */
	conversation_id?: unknown;
	/** A chat frame's body, read through `toChatMessage`. */
	message?: unknown;
	messages?: unknown;
	data?: { messages?: unknown[] };
	participants?: unknown;
	timestamp?: unknown;
	joined?: unknown;
	quit?: unknown;
};

/** Ephemeral agent status frame -- handled before chat-message routing so it never enters the message list. */
export function handleAgentStatus(data: IncomingWsFrame, ctx: MessageHandlerContext): boolean {
	if (data.type !== AGENT_STATUS_WS_TYPE) return false;
	const frame = data as IncomingWsFrame & AgentStatusFrame;
	if (frame.conversation_id && frame.conversation_id !== ctx.roomId) return true;
	ctx.onAgentStatus?.(frame);
	return true;
}

export function handleHistoryArray(data: Record<string, unknown>[], ctx: MessageHandlerContext): boolean {
	const messages = data.map((item) => toChatMessage(item, ctx.roomId)).filter(isNonNull);
	if (messages.length > 0) {
		ctx.onHistory?.(messages);
	}
	return true;
}

function extractMessageArray(frame: IncomingWsFrame): unknown[] | null {
	if (Array.isArray(frame.messages)) {
		// `Array.isArray` narrows to `any[]`; the container is widened to the `unknown[]` this function already returns, so
		// the per-item guard in `handleMessagesPayload` stays the thing that decides each element's shape.
		const messages: unknown[] = frame.messages;
		return messages;
	}
	const nested = frame.data;
	if (Array.isArray(nested?.messages)) {
		return nested.messages;
	}
	return null;
}

export function handleMessagesPayload(data: IncomingWsFrame, ctx: MessageHandlerContext): void {
	const messageArray = extractMessageArray(data);
	if (messageArray) {
		const history = messageArray
			.map((item) => (item && typeof item === 'object' ? toChatMessage(item as Record<string, unknown>, ctx.roomId) : null))
			.filter(isNonNull);
		if (history.length > 0) {
			ctx.onHistory?.(history);
		}
	}
}

export function handleParticipantsPayload(data: IncomingWsFrame, ctx: MessageHandlerContext): void {
	const { participants } = data;
	if (Array.isArray(participants)) {
		ctx.onParticipants?.(participants.map(toParticipant));
	}
}

export function handleJoinEvent(data: IncomingWsFrame, ctx: MessageHandlerContext): boolean {
	if (data.joined && typeof data.joined === 'object') {
		const participant = toParticipant(data.joined as Record<string, unknown>);
		ctx.onParticipantJoin?.(participant);
		return true;
	}
	return false;
}

export function handleQuitEvent(data: IncomingWsFrame, ctx: MessageHandlerContext): boolean {
	if (data.quit && typeof data.quit === 'object') {
		const quitData = data.quit as Record<string, unknown>;
		const quitId = coalesceStringAllowEmpty(quitData.ID, quitData.id);
		if (quitId) {
			ctx.onParticipantLeave?.(quitId);
		}
		return true;
	}
	return false;
}

/**
 * Marks THIS socket's room read. It carries NO conversation id, deliberately: the ChatRoom DO now scopes the write to
 * its own `getLogicalConversationId()`, because the update statement scopes on `WHERE id = ?` alone and a
 * client-supplied key therefore moved ANY conversation's read marker from a socket authorized for one room. Sending a
 * key the server ignores would leave a reader believing the client still chooses the target.
 */
function sendLastReadUpdate(socket: WebSocket | null, data: IncomingWsFrame): void {
	const { timestamp: tsRaw } = data;
	const timestamp = bareNumberOrNull(tsRaw);
	if (timestamp && isWebSocketConnected(socket) && socket) {
		const now = Date.now();
		if (Math.abs(now - timestamp) <= MESSAGE_TIMESTAMP_THRESHOLD_MS) {
			void sendWsMessage(socket, {
				type: 'updateLastRead',
				updateLastReadTime: true,
			});
		}
	}
}

export function handleChatMessage(data: IncomingWsFrame, ctx: MessageHandlerContext, socket: WebSocket | null): void {
	// toChatMessage already returns null when resolveMessage yields nothing, so a separate resolveMessage guard would parse the message twice.
	const chatMsg = toChatMessage(data, ctx.roomId);
	if (chatMsg) {
		ctx.onMessage?.(chatMsg, data);
		sendLastReadUpdate(socket, data);
	}
}
